import { DateTime, Interval } from 'luxon';
import { EnvironmentService } from '@services/environment-service/environment.service';
import {N0LowFareAmount} from "@models/responses/lowfare-availability-response.model";
import { N0Journey, PassengerTypeCode } from '@models/booking.models';
import { PassengerCriteria, SearchRequest, StationCriteria } from '@models/requests/search-request.model';
import { FilghtAvailability, FlightAvailabilityWeek } from '@models/availability.model';
import { DATE_FORMAT_YEAR_MONTH_DATE } from '@constants/date-format.constants';
import { N0SellKey } from '@models/requests/trip-sell-request.model';
import { N0TripResult, N0AvailableJourney } from '@models/responses/availability-response.model';
import { CabinClassCode } from '@constants/cabin.constants';
import { FlightsCopy } from '@models/cms/flights.model';
import { RouteScheduleDate } from '@models/requests/route-schedule-request.model';
import { TripType } from '@constants/home.constants';
import { Station } from '@models/responses/station-response.model';
import {AppCopy} from "@models/cms/app.model";
import {SearchWidgetCopy} from "@models/cms/search-widget.model";

export const getValidLowFareBeginDate = (
  beginDate: string,
  isMobile: boolean,
  isCalendar = false,
): DateTime => {
  const environment = EnvironmentService.environment;
  const newBeginDt = !isCalendar
    ? DateTime.fromISO(beginDate).minus({
        days: isMobile
          ? Number(environment.flights.lowFareRibbonDaysBeforeAndAfterMobile)
          : Number(environment.flights.lowFareRibbonDaysBeforeAndAfter),
      })
    : DateTime.fromISO(beginDate).startOf('month');
  return newBeginDt;
};

export const getValidLowFareEndDate = (
  beginDate: string,
  validBeginDt: DateTime,
  isMobile: boolean,
  isCalendar = false,
): DateTime => {
  const environment = EnvironmentService.environment;
  const expectedDateRange = getLowFareDatesCount(isMobile);
  const newEndDt = !isCalendar
    ? DateTime.fromISO(beginDate).plus({
        days: isMobile
          ? Number(environment.flights.lowFareRibbonDaysBeforeAndAfterMobile)
          : Number(environment.flights.lowFareRibbonDaysBeforeAndAfter),
      })
    : DateTime.fromISO(beginDate).endOf('month');
  const diffInDays = validBeginDt.diff(newEndDt, 'days').toObject().days;
  return isCalendar || diffInDays === expectedDateRange
    ? newEndDt
    : validBeginDt.plus({ days: expectedDateRange - 1 });
};

export const getValidLowFareDates = (
  searchDate: string,
  isMobile: boolean,
  isCalendar = false,
) => {
  const startDate = getValidLowFareBeginDate(searchDate, isMobile, isCalendar);
  const endDate = getValidLowFareEndDate(
    searchDate,
    startDate,
    isMobile,
    isCalendar,
  );

  return { startDate, endDate };
};

export const getLowFareDatesCount = (isMobile: boolean) => {
  const environment = EnvironmentService.environment;
  return (
    (isMobile
      ? Number(environment.flights.lowFareRibbonDaysBeforeAndAfterMobile)
      : Number(environment.flights.lowFareRibbonDaysBeforeAndAfter)) *
      2 +
    1
  );
};

export const countAvailabilityPassengersByType = (passengers: PassengerCriteria[], type: PassengerTypeCode) =>
  passengers
    .filter((p) => p.type === type)
    .reduce((acc, p) => acc + p.count, 0);

export const countAvailabilityPassengersExcludingType = (passengers: PassengerCriteria[], type: PassengerTypeCode) =>
  passengers
    .filter((p) => p.type !== type)
    .reduce((acc, p) => acc + p.count, 0);

export function markRandomDatesAsCurrentlyViewed(amounts: N0LowFareAmount[]): N0LowFareAmount[] {
  const availableDates = amounts.filter(a => a.fareAmount);
  const totalDates = availableDates.length;
  const percentage = 15;
  const datesToSelect = Math.ceil(totalDates * (percentage / 100));
  const selectedDates: N0LowFareAmount[] = [];
  const blockedDates: Map<string, boolean> = new Map();
  const maxAvailableDates = totalDates * 3;

  while (selectedDates.length < datesToSelect && blockedDates.size < maxAvailableDates) {
    const randomIndex = Math.floor(Math.random() * totalDates);
    const candidateFlight = availableDates[randomIndex]!;
    const departureDate = DateTime.fromISO(candidateFlight.departureDate).toISODate()!;

    if (!blockedDates.has(departureDate)) {
      candidateFlight.isCurrentlyViewed = true;
      selectedDates.push(candidateFlight);
      blockedDates.set(departureDate, true);

      const dateObj = DateTime.fromISO(departureDate);
      const prevDate = dateObj.minus({ days: 1 }).toISODate()!;
      const nextDate = dateObj.plus({ days: 1 }).toISODate()!;
      blockedDates.set(prevDate, true);
      blockedDates.set(nextDate, true);
    }
  }

  return amounts;
}

export const getMaxValueOf10PercentCheapest = (lowFareAmounts: N0LowFareAmount[]) => {
  if (lowFareAmounts.length === 0) {
    return null;
  }

  const filteredFareAmounts = lowFareAmounts.filter(s => !!s.roundedFareTotal && s.roundedFareTotal > 0)
  const sortedByRoundedFareTotal = [...filteredFareAmounts].sort((a, b) => a.roundedFareTotal - b.roundedFareTotal);
  const tenPercentIndex = Math.ceil(filteredFareAmounts.length * 0.1);
  const lowestTenPercent = sortedByRoundedFareTotal.slice(0, tenPercentIndex);
  if (lowestTenPercent.length > 0) {
    return Math.max(...lowestTenPercent.map(fare => fare.roundedFareTotal));
  }

  return null;
}


export const getWeeksAvailability = (lowFareAmounts: N0LowFareAmount[], cultureCode: string, minValidDate: DateTime | undefined, lowestFareMinValue: number | null, schedule: RouteScheduleDate[], handleWeek53: boolean): {weeksAvailability: FlightAvailabilityWeek, hasSalesFares: boolean, hasLowestFares: boolean} => {
  const weeksAvailability: FlightAvailabilityWeek = {};

  if (lowFareAmounts.length === 0) {
    return {weeksAvailability, hasSalesFares: false, hasLowestFares: false};
  }
  let hasSalesFares = false;
  let hasLowestFares = false;
  lowFareAmounts.forEach(fare => {
    const departureDate = DateTime.fromISO(fare.departureDate, {locale: cultureCode, zone: 'local'});
    const weekNumber = handleWeek53 ? departureDate.localWeekNumber%53 : departureDate.localWeekNumber;
    weeksAvailability[weekNumber] = weeksAvailability[weekNumber] ?? [];
    if (fare.isSaleFare) {
      hasSalesFares = true;
    }

    const isDateValid = (!minValidDate || departureDate >= minValidDate) && fare.roundedFareTotal > 0;
    const lowestFareCondition = isDateValid && !!lowestFareMinValue && fare.roundedFareTotal <= lowestFareMinValue;
    if (lowestFareCondition) {
      hasLowestFares = true;
    }

    const isScheduled = schedule.some(s => DateTime.fromJSDate(s.scheduleDay).toISO() === departureDate.toISO());

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    weeksAvailability[weekNumber]!.push({
      day: departureDate,
      price: fare.roundedFareTotal,
      isSaleFare: fare.isSaleFare,
      isDateValid,
      isLowestFare: lowestFareCondition,
      isScheduled
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstDepartureDate = DateTime.fromISO(lowFareAmounts[0]!.departureDate).setLocale(cultureCode);
  const startOfWeek = firstDepartureDate.startOf('week', {useLocaleWeeks: true});
  let intervals = Interval.fromDateTimes(startOfWeek.startOf("day"), firstDepartureDate.startOf("day"))
    .splitBy({ day: 1 })
    .map(d => d.start)
    .reverse();

  intervals.forEach(interval =>{
    if (interval) {
      const weekNumber = handleWeek53 ? interval.localWeekNumber%53 : interval.localWeekNumber;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weeksAvailability[weekNumber]!.unshift({
        day: interval,
        isSaleFare: false,
        isDateValid: false,
        isOtherMonth: true,
        isLowestFare: false,
        isScheduled: false,
      });
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const lastDepartureDate = DateTime.fromISO(lowFareAmounts.slice(-1)[0]!.departureDate).setLocale(cultureCode);
  const endOfWeek = lastDepartureDate.endOf('week', {useLocaleWeeks: true});
  intervals = Interval.fromDateTimes(lastDepartureDate.endOf("day"), endOfWeek.endOf("day"),)
    .splitBy({ day: 1 })
    .map(d => d.end)

    intervals.forEach(interval =>{
      if (interval) {
        const weekNumber = handleWeek53 ? interval.localWeekNumber%53 : interval.localWeekNumber;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        weeksAvailability[weekNumber]!.push({
          day: interval,
          isSaleFare: false,
          isDateValid: false,
          isOtherMonth: true,
          isLowestFare: false,
          isScheduled: false,
        });
      }
    });

  return { weeksAvailability, hasSalesFares, hasLowestFares };
};


export const updateSearchCriteria = (searchAvailabilityInput: SearchRequest, searchInput: SearchRequest, tripIndex: number, beginDate: DateTime, selectedDates: DateTime[]) => {

  searchAvailabilityInput.criteria = searchAvailabilityInput.criteria.map((c, index) => {
    const criteriaDate = DateTime.fromISO(c.beginDate);
    if (!c.beginDate || index === tripIndex || (index < tripIndex && criteriaDate > beginDate) || (index > tripIndex && criteriaDate < beginDate)) {
      return {
        ...c,
        beginDate: beginDate.toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
      };
    }
    return c;
  });

  searchInput.criteria = searchInput.criteria.map((c, index) => {
    const criteriaDate = DateTime.fromISO(c.beginDate);
    if (!c.beginDate || index === tripIndex || (index < tripIndex && criteriaDate > beginDate) || (index > tripIndex && criteriaDate < beginDate)) {
      return {
        ...c,
        beginDate: beginDate.toFormat(DATE_FORMAT_YEAR_MONTH_DATE),
      };
    }
    return c;
  });

  selectedDates[tripIndex] = beginDate;
  selectedDates = selectedDates.filter((date, index) => tripIndex >= index  || (tripIndex < index && date >= beginDate));
  return { searchInput, searchAvailabilityInput, selectedDates };
}

export const createBookingSelection = (tripResults: N0TripResult[] | undefined, bookingJourneys: N0Journey[]) => {
  const journeys = tripResults?.flatMap(r => r.trips).flatMap(t => t.journeysAvailableByMarket ?? []) ?? [];
  const matchedJourneys: (N0AvailableJourney | null)[] = [];
  const sellKeys: N0SellKey[] = [];
  bookingJourneys.forEach(bj => {
    const journey = journeys.find(j => j.journeyKey === bj.journeyKey);
    if (journey) {
      matchedJourneys.push(journey);
      const fare = journey.fares.find(f => f.details.productClass === bj.productClass);
      if (fare) {
        sellKeys.push({
          journeyKey: bj.journeyKey,
          fareAvailabilityKey: fare.fareAvailabilityKey,
          bundleCode: fare.details.bundleCode,
          originalJourneyKey: bj.journeyKey,
          originalBundleCode: bj.productClass,
        } as N0SellKey);
      }
    }
  });
  return { matchedJourneys, sellKeys };
}

export const createFlightAvailability = (
  index: number,
  flightsCopy: FlightsCopy,
  selectedDates: DateTime<boolean>[],
  lowFareAmounts: N0LowFareAmount[],
  cultureCode: string,
  criteria: StationCriteria,
  cabinClass: CabinClassCode,
  schedule: RouteScheduleDate[],
  stations: Station[] | null,
  tripType: TripType,
) => {
  const handleWeek53 = DateTime.fromISO(criteria.beginDate, { locale: cultureCode, zone: 'local' }).month === 1;
  const iconDirection = index > 0 ? 'fa-kit fa-outbound fa-flip-horizontal' : 'fa-kit fa-outbound';
  const textDirectionSelected = index > 0 ? flightsCopy.selectReturn : flightsCopy.outboundText;
  const returnText = selectedDates.length > 0 ? flightsCopy.selectReturnPostDepart : flightsCopy.selectReturn;
  const textDirectionNotSelected = index > 0 ? returnText : flightsCopy.selectDeparture;
  const ondText = (stations && getStationLabel(criteria, stations)) ?? flightsCopy.flight + (index + 1);
  const tenPercentCheapestLimit = getMaxValueOf10PercentCheapest(lowFareAmounts);
  const availability = getWeeksAvailability(lowFareAmounts, cultureCode, selectedDates[index - 1], tenPercentCheapestLimit, schedule, handleWeek53);
  const localBeginDate = DateTime.fromISO(criteria.beginDate, { locale: cultureCode, zone: 'local' });
  const flightAvailability: FilghtAvailability = {
    preferredCabinPricing: cabinClass,
    tabIcon: selectedDates[index] ? 'fa-kit fa-check' : iconDirection,
    tabTitle: tripType === TripType.multiCity ? ondText : selectedDates[index] ? textDirectionSelected : textDirectionNotSelected,
    searchMonth: localBeginDate,
    weeksAvailability: availability.weeksAvailability,
    hasSaleFares: availability.hasSalesFares,
    hasLowestFares: availability.hasLowestFares,
    origin: criteria.origin,
    destination: criteria.destination,
    tripIndex: index,
    selectFlightText: index > 0 ? flightsCopy.selectReturningFlight : flightsCopy.selectDepartureFlight,
    noFaresAvailable: lowFareAmounts.length === 0
  };
  return flightAvailability;
}

export const getStationLabel = (criteria: StationCriteria, stations: Station[]) => {
  return stations.find(s => s.stationCode === criteria.origin)?.fullName + ' - ' + stations.find(s => s.stationCode === criteria.destination)?.fullName;
}

export const scheduleLoadedThru = (cmsCopy: SearchWidgetCopy | null | undefined): string | undefined =>  {
  if (cmsCopy?.scheduleLoadedThruDays && cmsCopy?.scheduleLoadedThruDate) {
    if (isNaN(+cmsCopy.scheduleLoadedThruDays)) {
      return cmsCopy.scheduleLoadedThruDate;
    }

    const thruDays = DateTime.now()
      .plus({days: +cmsCopy.scheduleLoadedThruDays})
      .startOf('day');
    const thruDate = DateTime.fromFormat(
      cmsCopy.scheduleLoadedThruDate,
      DATE_FORMAT_YEAR_MONTH_DATE,
    ).startOf('day');

    return thruDays < thruDate
      ? thruDays.toFormat(DATE_FORMAT_YEAR_MONTH_DATE)
      : thruDate.toFormat(DATE_FORMAT_YEAR_MONTH_DATE);
  }
}
